<!-- Please note this component does not properly support transparent images. -->
<!-- SurfacePostAttachment or SurfaceAttachmentPreview wrap this component with the proper logic -->

<script lang="ts" setup>
import { attachmentLoadingAnimationColorTailwindClass } from '@@/bits/attachments'
import device from '@@/bits/device'
import overflowBorderRadiusTransformFixStyle from '@@/bits/overflow_border_radius_transform_fix'
import { postColorValues } from '@@/bits/post_color'
import BluePurpleLanterns from '@@/images/blue_purple_lanterns.png'
import type { PostColor } from '@@/types'
import BaseImage from '@@/vuecomponents/base_image.vue'
import { processedSrcSet, processedUrl, proxiedUrl } from '@padlet/vivaldi-client'
import { sample } from 'es-toolkit'
import { computed, ref } from 'vue'

const {
  width,
  height,
  resizeStrategy = 'crop',
  aspectRatio,
  alt,
  title = '',
  lazyLoading = true,
  proxyImage = false,
  processImage = false,
  placeholderColor = 'transparent',
  placeholderColorValue,
  fallbackToPlaceholder = false,
  placeholderAnimationVariant,
  dataTestid,
  flipDirection,
  objectPosition,
  fetchPriority = 'auto',
  disablePointerEvents = true,
  maxImageWidth,
  src,
  originalImageWidth,
  originalImageHeight,
} = defineProps<{
  /**
   * URL for image.
   */
  src: string
  /**
   * Original image width.
   */
  originalImageWidth: number
  /**
   * Original image height.
   */
  originalImageHeight: number
  /**
   * Resulting component width.
   */
  width?: number
  /**
   * Resulting component height. Ignored for calculation.
   * Only use to send to Vivaldi if processImage is true.
   */
  height?: number
  /**
   * Resize strategy. Ignored if no aspect ratio is provided.
   * @values crop, stretch, fit.
   */
  resizeStrategy?: 'crop' | 'stretch' | 'fit'
  /**
   * If specified, thumbnail height will be computed based on this value.
   * Value should be `width / height`, i.e. `2` will be wide and `0.5` will be tall.
   */
  aspectRatio?: number
  /**
   * Alt text for the image.
   */
  alt?: string
  /**
   * Title to display when the image fails to load.
   * This is used as the image alt text if no alt prop is given.
   */
  title?: string
  /**
   * Determines if we should load the image lazily.
   */
  lazyLoading?: boolean
  /**
   * Determines whether image should be proxied to vivaldi for caching.
   */
  proxyImage?: boolean
  /**
   * Determines whether image should be processed before loading.
   */
  processImage?: boolean
  /**
   * Placeholder color for the thumbnail.
   * @values red, blue, green, yellow, purple, pink, cyan, orange, transparent, random.
   */
  placeholderColor?: string
  /**
   * Placeholder 'background-color' value.
   */
  placeholderColorValue?: string
  /**
   * If true, display placeholder image when original image fails to load
   * Otherwise, show image title on top of placeholder solid background.
   */
  fallbackToPlaceholder?: boolean
  /**
   * If passed, will enable animation for the placeholder.
   */
  placeholderAnimationVariant?: 'dark' | 'light'
  /**
   * data-testid.
   */
  dataTestid?: string
  /**
   * Flip the image.
   */
  flipDirection?: 'horizontal' | 'vertical'
  /**
   * Determines where the image position is when used in conjunction with object-fit
   * Otherwise the image will always be cropped from the center.
   */
  objectPosition?: string
  /**
   * Fetch priority for image
   */
  fetchPriority?: 'auto' | 'high' | 'low'
  /**
   * Determines whether to add the `pointer-events-none` class to disable the image drag event.
   */
  disablePointerEvents?: boolean

  maxImageWidth?: number
}>()

const emit = defineEmits<{
  (e: 'load'): void
  (e: 'error', error: Error): void
}>()

const loaded = ref(false)
const error = ref(false)

const errorPlaceholderImage = BluePurpleLanterns

const onLoad = (): void => {
  loaded.value = true
  emit('load')
}

const onError = (e: Error): void => {
  error.value = true
  emit('error', e)
}

const overflowBorderRadiusStyle = computed(() => overflowBorderRadiusTransformFixStyle(device.safari))

const bgColorClass = computed(() => {
  if (placeholderColorValue) return ''

  if (loaded.value) return ''

  if (
    placeholderAnimationVariant &&
    postColorValues.includes(placeholderColor === 'default' ? null : (placeholderColor as PostColor))
  ) {
    return attachmentLoadingAnimationColorTailwindClass({
      postColor: placeholderColor as PostColor,
      isLightColorScheme: placeholderAnimationVariant === 'light',
    })
  }

  const colorClassMap = {
    red: 'bg-bgi-red',
    blue: 'bg-bgi-blue',
    green: 'bg-bgi-green',
    yellow: 'bg-bgi-yellow',
    purple: 'bg-bgi-purple',
    pink: 'bg-bgi-pink',
    cyan: 'bg-bgi-cyan',
    orange: 'bg-bgi-orange',
    grey: 'bg-highway-200',
  }

  if (placeholderColor === 'random') {
    return sample(Object.values(colorClassMap)) ?? ''
  } else {
    return colorClassMap[placeholderColor] || ''
  }
})

const baseProcessingOptions = computed(() => {
  const o: { width: number; height?: number; ar?: number } = { width: width as number }
  if (height) o.height = height
  if (resizeStrategy === 'stretch' && aspectRatio) o.ar = aspectRatio
  return o
})

const url = computed(() => {
  if (processImage) {
    const processingOptions = { ...baseProcessingOptions.value }
    if (maxImageWidth !== undefined && width !== undefined && maxImageWidth > width) {
      // If max image width is set, we fetch the image at the max width and scale it down.
      // This is so we can increase cache hit rate for wall previews, at the cost of a slightly larger image file.
      processingOptions.width = maxImageWidth
    }
    return processedUrl(src, processingOptions)
  }
  return proxyImage ? proxiedUrl(src) : src
})

// srcset intentionally uses display width (not maxImageWidth) to serve images at exact rendered dimensions.
const srcset = computed(() => {
  if (!processImage) return undefined
  return processedSrcSet(src, baseProcessingOptions.value)
})

const originalImageAspectRatio = computed(() => originalImageWidth / originalImageHeight)

const dimensions = computed(() => {
  // Width is fixed except when resizeStrategy === 'fit' (CASE 3)
  let cropFrameWidth = width || 0
  let unit: 'px' | '%' = 'px'

  // These are decided based on the resizing strategy.
  let cropFrameHeight: number
  let cropFrameAspectRatio: number
  let imageElementHeight: number
  let imageElementWidth: number

  // CASE 1: Crop to fit aspect ratio
  if (resizeStrategy === 'crop' && aspectRatio) {
    // Try to fit as much of the image in the crop frame as possible.
    cropFrameAspectRatio = aspectRatio

    // All cropFrameHeight calculation in this method should use Math.ceil.
    // Reason:
    // Browsers can display HTML elements with fractional height.
    // In Vivaldi, when calculating height using width and aspect ratio, we use
    // Math.ceil (see [1] and [2]). So the resulting image height will never be
    // fractional. But the <div> in this component can have fractional height.
    // -> By using Math.ceil, we ensure the <div> will have the same height as
    // the image.
    //
    // [1]: https://github.com/padlet/mozart/blob/21a2867e9c2abf5e71db0bb6c3eaa6b86b6cd904/services/vivaldi/src/vips/fill.js#L30
    // [2]: https://github.com/padlet/mozart/blob/21a2867e9c2abf5e71db0bb6c3eaa6b86b6cd904/services/vivaldi/src/vips/limit_fill.js#L39
    cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
    const originalHasWiderAspectRatio = originalImageAspectRatio.value > aspectRatio
    if (originalHasWiderAspectRatio) {
      imageElementHeight = cropFrameHeight
      imageElementWidth = originalImageAspectRatio.value * cropFrameHeight
    } else {
      imageElementWidth = cropFrameWidth
      imageElementHeight = imageElementWidth / originalImageAspectRatio.value
    }

    // CASE 2: Stretch to fit aspect ratio
  } else if (resizeStrategy === 'stretch' && aspectRatio) {
    // Image element and crop frame both have the same dimensions.
    cropFrameAspectRatio = aspectRatio
    cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
    imageElementWidth = cropFrameWidth
    imageElementHeight = cropFrameHeight

    // CASE 3: Fit parent's dimensions
  } else if (resizeStrategy === 'fit') {
    // Fit the image to its parent element regardless of aspect ratio.
    cropFrameAspectRatio = originalImageAspectRatio.value
    cropFrameWidth = 100
    cropFrameHeight = 100
    imageElementWidth = 100
    imageElementHeight = 100
    unit = '%'

    // CASE 4: Scale, keeping original aspect ratio
  } else {
    // Maintain the same ratio as the original, but scale down proportionately if need be.
    cropFrameAspectRatio = originalImageAspectRatio.value
    cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
    imageElementWidth = cropFrameWidth
    imageElementHeight = cropFrameHeight
  }

  return {
    cropFrameWidth,
    cropFrameWidthString: `${cropFrameWidth}${unit}`,
    cropFrameHeight,
    cropFrameHeightString: `${cropFrameHeight}${unit}`,
    cropFrameAspectRatio,
    imageElementWidth,
    imageElementWidthString: `${imageElementWidth}${unit}`,
    imageElementHeight,
    imageElementHeightString: `${imageElementHeight}${unit}`,
  }
})

const baseImageStyle = computed(() => {
  if (resizeStrategy === 'fit') {
    return {
      width: dimensions.value.imageElementWidthString,
      height: dimensions.value.imageElementHeightString,
      objectFit: 'cover',
      objectPosition: objectPosition,
    }
  }
  if (objectPosition) {
    return {
      objectFit: 'cover',
      objectPosition: objectPosition,
    }
  }
  return {}
})
</script>

<template>
  <div
    :class="[
      // ResizableMixin
      'image-thumbnail-crop-frame',
      // Crop
      'overflow-hidden',
      // Alignment
      'flex',
      objectPosition === 'left top' || objectPosition === 'center top' ? 'items-start' : 'items-center',
      objectPosition === 'left top' ? 'justify-start' : 'justify-center',
      bgColorClass,
      disablePointerEvents && 'pointer-events-none',
    ]"
    :style="{
      ...overflowBorderRadiusStyle,
      width: dimensions.cropFrameWidthString,
      height: dimensions.cropFrameHeightString,
      'background-color': placeholderColorValue,
    }"
    :data-aspect-ratio="dimensions.cropFrameAspectRatio"
  >
    <base-image
      v-if="!error"
      :src="url"
      :srcset="srcset"
      :class="[
        // ResizableMixin
        'image-thumbnail-image',
        flipDirection === 'horizontal' && 'scale-x-[-1]',
        flipDirection === 'vertical' && 'scale-y-[-1]',
      ]"
      :style="baseImageStyle"
      :alt="alt != undefined ? alt : title"
      :data-testid="dataTestid"
      :height="dimensions.imageElementHeight"
      :width="dimensions.imageElementWidth"
      :loading="lazyLoading ? 'lazy' : 'eager'"
      :fetch-priority="fetchPriority"
      @load="onLoad"
      @error="onError"
    ></base-image>
    <base-image
      v-else-if="fallbackToPlaceholder"
      :src="errorPlaceholderImage"
      :class="[
        // ResizableMixin
        'image-thumbnail-image',
      ]"
      :style="baseImageStyle"
      :alt="alt != undefined ? alt : title"
      :data-testid="dataTestid"
      :height="dimensions.imageElementHeight"
      :width="dimensions.imageElementWidth"
      :loading="lazyLoading ? 'lazy' : 'eager'"
      :fetch-priority="fetchPriority"
    />
    <p v-else>{{ title }}</p>
  </div>
</template>
